/**
 * rtIsocenterModule.js — Этап 5: изоцентр мишени и стереотаксические координаты.
 *
 * Ядро (rtIsocenterCore) проверено: центр масс структуры (совпадает с центром
 * сферы до 0.3 мм даже на наклоне), анатомические оси R/L·A/P·S/I.
 * Здесь — связь с загруженными структурами, показ координат и переход к изоцентру.
 */

import { log } from '../utils/logger.js';
import { registerAPIs } from '../core/viewerAPI.js';
import { cornerstone, getRenderingEngine } from '../core/viewportState.js';
import { getStructuresRaw } from './rtStructModule.js';
import { structureCentroidWorld, anatomical, formatAnatomical } from './rtIsocenterCore.js';
import { helpIcon } from './rtKnowledgeModule.js';   // [12.0.17] значок «?» у заголовка

let _ref = [0, 0, 0];   // опора (по умолчанию — начало координат DICOM = изоцентр сканера)

/** Изоцентр структуры (мир LPS) + анатомические координаты от опоры. */
export function computeIsocenter(roiNumber) {
  const list = getStructuresRaw().filter(s => s.contours && s.contours.length);
  const st = (roiNumber != null ? list.find(s => s.roiNumber === roiNumber) : list[0]);
  if (!st) throw new Error('Нет структуры с контурами');
  const { center, normal, used, skipped } = structureCentroidWorld(st.contours);
  if (skipped) log.warn(`[ISO] ${st.name}: учтено ${used} контуров главной плоскости, отброшено ${skipped}.`);
  return { name: st.name, roiNumber: st.roiNumber, world: center, normal, used, skipped,
           anat: anatomical(center, _ref), ref: _ref.slice() };
}

export function setReference(pt) { if (Array.isArray(pt) && pt.length === 3) _ref = pt.map(Number); }
export function getReference() { return _ref.slice(); }

function _mprViewports() {
  try {
    return (getRenderingEngine()?.getViewports?.() || [])
      .filter(vp => String(vp?.type || '').toLowerCase().includes('orthographic'));
  } catch (e) { return []; }
}

/** Опора из текущего центра окна (фокальная точка) — best-effort. */
export function setReferenceFromView() {
  const vp = _mprViewports()[0];
  const cam = vp?.getCamera?.();
  if (cam?.focalPoint) { _ref = Array.from(cam.focalPoint); log.info('[ISO] Опора из окна:', _ref.map(v => v.toFixed(1))); }
  return _ref.slice();
}

/** Перевести MPR-окна в изоцентр (центрировать срезы на точке). */
export function jumpToIsocenter(world) {
  for (const vp of _mprViewports()) {
    try {
      const cam = vp.getCamera?.(); if (!cam) continue;
      const n = cam.viewPlaneNormal || [0, 0, 1];
      const d = Math.hypot(cam.position[0] - cam.focalPoint[0], cam.position[1] - cam.focalPoint[1], cam.position[2] - cam.focalPoint[2]) || 100;
      vp.setCamera?.({ focalPoint: world, position: [world[0] + n[0] * d, world[1] + n[1] * d, world[2] + n[2] * d] });
    } catch (e) { /* */ }
  }
  try { getRenderingEngine()?.render?.(); } catch (e) { /* */ }
}

// ── UI ──────────────────────────────────────────────────────────────────────
export function mountIsocenterUI() {
  if (document.getElementById('iso-box')) return;
  const host = document.getElementById('rtstruct-panel') || document.body;
  const box = document.createElement('div');
  box.id = 'iso-box';
  box.className = 'rt-sec';
  box.innerHTML = `
    <details class="rt-fold" open>
      <summary class="rt-sec-head"><span class="rt-sec-title">Изоцентр мишени${helpIcon('three-planes')}</span></summary>
    <label class="rt-field"><span>Структура</span>
      <select id="iso-roi" class="rt-select"></select></label>
    <button id="iso-run" class="rt-btn rt-btn--primary" style="margin-top:8px;">Найти изоцентр</button>
    <div id="iso-out" class="rt-out"></div>
    <div class="rt-field"><span>Опора (0,0,0 = изоцентр сканера)</span></div>
    <div class="rt-row">
      <button id="iso-ref-view" class="rt-btn rt-btn--sm">Опора из окна</button>
      <button id="iso-ref-zero" class="rt-btn rt-btn--sm">Сброс опоры</button>
    </div>
    </details>`;
  host.appendChild(box);

  const sel = box.querySelector('#iso-roi'), out = box.querySelector('#iso-out');
  const refresh = () => {
    const list = getStructuresRaw().filter(s => s.contours && s.contours.length);
    sel.innerHTML = list.map(s => `<option value="${s.roiNumber}">${s.name}</option>`).join('')
      || '<option value="">— нет структур —</option>';
  };
  refresh(); box.addEventListener('mouseenter', refresh);

  const show = () => {
    const roi = parseInt(sel.value, 10);
    try {
      const r = computeIsocenter(Number.isNaN(roi) ? null : roi);
      const w = r.world;
      out.innerHTML = `<b>${r.name}</b><br>
        мир (LPS): <b>${w[0].toFixed(1)}, ${w[1].toFixed(1)}, ${w[2].toFixed(1)}</b> мм<br>
        анатом. от опоры: <b>${formatAnatomical(r.anat)}</b><br>
        расстояние от опоры: <b>${r.anat.dist.toFixed(1)}</b> мм
        ${r.skipped ? `<div class="rt-hint rt-hint--todo">Учтено <b>${r.used}</b>
          контуров одной плоскости, отброшено <b>${r.skipped}</b> в других проекциях. Изоцентр
          посчитан по главной стопке.</div>` : ''}
        <button id="iso-jump" class="rt-btn rt-btn--sm" style="margin-top:8px;">Перейти к изоцентру</button>`;
      out.querySelector('#iso-jump').onclick = () => jumpToIsocenter(w);
      log.info(`[ISO] ${r.name}: LPS [${w.map(v => v.toFixed(1))}], ${formatAnatomical(r.anat)}`);
    } catch (e) { out.textContent = '❌ ' + e.message; }
  };
  box.querySelector('#iso-run').onclick = show;
  box.querySelector('#iso-ref-view').onclick = () => { setReferenceFromView(); show(); };
  box.querySelector('#iso-ref-zero').onclick = () => { setReference([0, 0, 0]); show(); };
}

export function initIsocenter() {
  try {
    mountIsocenterUI();
    registerAPIs({ computeIsocenter, setReference, getReference, setReferenceFromView, jumpToIsocenter });
    log.info('[ISO] Модуль изоцентра инициализирован — 1.0 (Этап 5)');
  } catch (e) { log.error('[ISO] init failed:', e); }
}

export default { initIsocenter, computeIsocenter, setReference, getReference, jumpToIsocenter };
